import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Upload,
  Loader2,
  FileText,
  X,
  AlertCircle,
  CheckCircle2,
  XCircle,
  RotateCcw,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';

export type PresentationUploadResult =
  | { ok: true; truncationMessage?: string | null }
  | { ok: false; error: string };

interface PresentationUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Uploads a single file. Called once per file. Must resolve (never throw)
   * so per-file failures can be rendered independently.
   */
  onUploadOne: (
    file: File,
    opts: { title?: string; description?: string }
  ) => Promise<PresentationUploadResult>;
  /**
   * Called once per upload run after every file in that run has settled.
   * Fires for the initial submit, and again for each Retry Failed run.
   * Used by the parent to invalidate the decks query and show a summary toast.
   */
  onComplete: (summary: {
    succeeded: number;
    failed: number;
    truncationMessages: string[];
  }) => void;
}

const ACCEPTED_PRESENTATION_TYPES = [
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
  'application/pdf', // .pdf
];
const ACCEPTED_EXTENSIONS = ['.pptx', '.pdf'];
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB per file
const MAX_FILES_PER_BATCH = 20;
const MAX_PARALLEL_UPLOADS = 3;

type ItemStatus = 'pending' | 'uploading' | 'success' | 'failed';

interface UploadItem {
  id: string;
  file: File;
  status: ItemStatus;
  error?: string;
  truncationMessage?: string;
  title?: string;
  description?: string;
  detailsOpen?: boolean;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const validateFile = (file: File): string | null => {
  const extension = '.' + (file.name.split('.').pop()?.toLowerCase() ?? '');
  const isValidExtension = ACCEPTED_EXTENSIONS.includes(extension);
  const isValidType = ACCEPTED_PRESENTATION_TYPES.includes(file.type);

  if (!isValidExtension && !isValidType) {
    return 'invalid type';
  }
  if (file.size > MAX_FILE_SIZE) {
    return 'exceeds 50MB';
  }
  return null;
};

// Cursor-based worker pool: `limit` coroutines pull from a shared index so
// each upload starts as soon as a worker is free. Avoids batching everything
// into rigid chunks of `limit` size.
const runWithConcurrency = async (
  total: number,
  limit: number,
  task: (index: number) => Promise<void>,
): Promise<void> => {
  if (total === 0) return;
  let cursor = 0;
  const workerCount = Math.min(Math.max(limit, 1), total);
  const workers = Array.from({ length: workerCount }, async () => {
    while (true) {
      const i = cursor;
      cursor += 1;
      if (i >= total) return;
      await task(i);
    }
  });
  await Promise.all(workers);
};

export const PresentationUploadDialog: React.FC<PresentationUploadDialogProps> = ({
  open,
  onOpenChange,
  onUploadOne,
  onComplete,
}) => {
  const [items, setItems] = useState<UploadItem[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const pendingCount = useMemo(
    () => items.filter((i) => i.status === 'pending').length,
    [items],
  );
  const succeededCount = useMemo(
    () => items.filter((i) => i.status === 'success').length,
    [items],
  );
  const failedCount = useMemo(
    () => items.filter((i) => i.status === 'failed').length,
    [items],
  );
  // Items actively being uploaded right now. Excludes already-succeeded rows
  // from prior runs so the in-flight banner reads correctly during Retry Failed.
  const inFlightCount = useMemo(
    () => items.filter((i) => i.status === 'pending' || i.status === 'uploading').length,
    [items],
  );

  const canEditFiles = !isUploading && !isComplete;
  const isSingleFile = items.length === 1;

  const resetState = useCallback(() => {
    setItems([]);
    setValidationError(null);
    setIsDragging(false);
    setIsUploading(false);
    setIsComplete(false);
  }, []);

  const handleDialogOpenChange = (next: boolean) => {
    // Prevent ESC / backdrop close while uploads are in flight so no request
    // is orphaned mid-stream.
    if (!next && isUploading) return;
    if (!next) resetState();
    onOpenChange(next);
  };

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      if (!canEditFiles) return;

      const incomingArr = Array.from(incoming);
      if (incomingArr.length === 0) return;

      const rejected: string[] = [];
      const accepted: UploadItem[] = [];

      for (const file of incomingArr) {
        const reason = validateFile(file);
        if (reason) {
          rejected.push(`${file.name} (${reason})`);
          continue;
        }
        accepted.push({
          id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
          file,
          status: 'pending',
        });
      }

      // Compute overflow synchronously so the validation message is correct
      // before setValidationError runs — the setItems updater is deferred to
      // the render phase and mutating `rejected` inside it would race.
      const availableSlots = Math.max(0, MAX_FILES_PER_BATCH - items.length);
      const toAccept = accepted.slice(0, availableSlots);
      const overflow = accepted.length - toAccept.length;
      if (overflow > 0) {
        rejected.push(`${overflow} file(s) over the ${MAX_FILES_PER_BATCH}-per-upload limit`);
      }

      setItems((prev) => [...prev, ...toAccept]);
      setValidationError(rejected.length ? `Skipped: ${rejected.join(', ')}` : null);

      // Reset native input so selecting the same file again fires onChange.
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [canEditFiles, items.length],
  );

  const removeItem = (id: string) => {
    if (!canEditFiles) return;
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (!canEditFiles) return;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (canEditFiles) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const updateItemStatus = useCallback(
    (id: string, patch: Partial<UploadItem>) => {
      setItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, ...patch } : item)),
      );
    },
    [],
  );

  // Shared upload runner. Both the initial submit and the Retry Failed action
  // funnel through here so the worker pool, status updates, and completion
  // semantics stay in one place.
  const performUploads = useCallback(
    async (
      targets: Array<{
        id: string;
        file: File;
        opts: { title?: string; description?: string };
      }>,
    ) => {
      if (targets.length === 0) return;

      setValidationError(null);
      setIsUploading(true);

      const collectedTruncations: string[] = [];
      // Local counters are safe to mutate from parallel workers under JS's
      // single-threaded model and give us fresh totals without reading state
      // (which is async) after the batch settles.
      let successCount = 0;
      let failCount = 0;

      await runWithConcurrency(targets.length, MAX_PARALLEL_UPLOADS, async (i) => {
        const target = targets[i];
        updateItemStatus(target.id, { status: 'uploading', error: undefined });
        try {
          const result = await onUploadOne(target.file, target.opts);
          if (result.ok) {
            successCount += 1;
            updateItemStatus(target.id, {
              status: 'success',
              truncationMessage: result.truncationMessage ?? undefined,
            });
            if (result.truncationMessage) {
              collectedTruncations.push(result.truncationMessage);
            }
          } else {
            failCount += 1;
            updateItemStatus(target.id, {
              status: 'failed',
              error: result.error || 'Upload failed',
            });
          }
        } catch (err) {
          // Defensive: onUploadOne is contracted to resolve, but we never want a
          // thrown error to leave the worker pool in an inconsistent state.
          failCount += 1;
          updateItemStatus(target.id, {
            status: 'failed',
            error: err instanceof Error ? err.message : 'Upload failed',
          });
        }
      });

      setIsUploading(false);

      onComplete({
        succeeded: successCount,
        failed: failCount,
        truncationMessages: collectedTruncations,
      });

      // On full success the parent's toast is the confirmation, so close the
      // dialog automatically. On partial failure keep it open so the user can
      // see which rows failed and retry them via the Retry Failed button.
      if (failCount === 0 && successCount > 0) {
        resetState();
        onOpenChange(false);
      } else {
        setIsComplete(true);
      }
    },
    [onUploadOne, onComplete, onOpenChange, resetState, updateItemStatus],
  );

  const handleSubmit = async () => {
    if (isUploading || isComplete) return;
    if (items.length === 0) {
      setValidationError('Please select at least one presentation file.');
      return;
    }

    // Snapshot only items that still need uploading. On the first run every
    // item is pending so this is the full list; the filter exists so retry
    // doesn't re-upload already-succeeded rows. Title/description are read
    // from the item itself — the single-file UI writes directly into
    // items[0], so there's no separate top-level state to reconcile.
    const targets = items
      .filter((item) => item.status === 'pending')
      .map((item) => ({
        id: item.id,
        file: item.file,
        opts: {
          title: item.title?.trim() || undefined,
          description: item.description?.trim() || undefined,
        },
      }));

    if (targets.length === 0) return;

    await performUploads(targets);
  };

  const handleRetryFailed = async () => {
    if (isUploading || failedCount === 0) return;

    const failedItems = items.filter((item) => item.status === 'failed');
    if (failedItems.length === 0) return;

    const targets = failedItems.map((item) => ({
      id: item.id,
      file: item.file,
      opts: {
        title: item.title?.trim() || undefined,
        description: item.description?.trim() || undefined,
      },
    }));

    // Flip failed rows back to pending and clear the completion banner before
    // re-entering the in-flight state. performUploads will move them through
    // uploading → success/failed again. These updates batch with the
    // setIsUploading(true) inside performUploads, so canEditFiles stays
    // false across the transition.
    setItems((prev) =>
      prev.map((item) =>
        item.status === 'failed'
          ? { ...item, status: 'pending', error: undefined }
          : item,
      ),
    );
    setIsComplete(false);

    await performUploads(targets);
  };

  const handleClose = () => {
    if (isUploading) return;
    resetState();
    onOpenChange(false);
  };

  const renderItemIcon = (status: ItemStatus) => {
    switch (status) {
      case 'uploading':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-300" />;
      case 'success':
        return <CheckCircle2 className="h-4 w-4 text-success" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-destructive" />;
      default:
        return <FileText className="h-4 w-4 text-muted-foreground" />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload Presentations
          </DialogTitle>
          <DialogDescription>
            Upload one or more PowerPoint or PDF presentations to add to your content library.
          </DialogDescription>
        </DialogHeader>

        <div className="min-w-0 space-y-4 py-4">
          {validationError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}

          {isUploading && inFlightCount > 0 && (
            <Alert>
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertDescription>
                Uploading {inFlightCount} presentation{inFlightCount === 1 ? '' : 's'}… Slide analysis continues in the background after each upload finishes.
              </AlertDescription>
            </Alert>
          )}

          {isComplete && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {`${succeededCount} uploaded, ${failedCount} failed.`}
              </AlertDescription>
            </Alert>
          )}

          {/* Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => canEditFiles && fileInputRef.current?.click()}
            className={`
              border-2 border-dashed rounded-lg p-6 text-center transition-colors
              ${canEditFiles ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}
              ${isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/30 hover:border-primary/50'}
            `}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_EXTENSIONS.join(',')}
              multiple
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  addFiles(e.target.files);
                }
              }}
              className="hidden"
              disabled={!canEditFiles}
            />
            <Upload className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm font-medium">Drag & drop presentation files here</p>
            <p className="text-xs text-muted-foreground mt-1">
              or click to browse (PPTX, PDF up to 50MB each, up to {MAX_FILES_PER_BATCH} files)
            </p>
          </div>

          {/* File list */}
          {items.length > 0 && (
            <div className="space-y-2 rounded-lg border border-[hsl(var(--input))] bg-muted/30 p-2">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="rounded-md bg-background px-3 py-2 text-sm"
                >
                  <div className="flex items-center gap-2">
                    <div className="shrink-0">{renderItemIcon(item.status)}</div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{item.file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(item.file.size)}
                        {item.status === 'failed' && item.error && (
                          <span className="text-destructive"> · {item.error}</span>
                        )}
                        {item.status === 'success' && item.truncationMessage && (
                          <span className="text-amber-700 dark:text-amber-300">
                            {' '}
                            · {item.truncationMessage}
                          </span>
                        )}
                      </p>
                    </div>
                    {canEditFiles && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeItem(item.id);
                        }}
                        aria-label={`Remove ${item.file.name}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {!isSingleFile && (
                    <div className="mt-2 pl-6">
                      {(item.detailsOpen || item.title || item.description) ? (
                        <div className="space-y-2">
                          <div className="space-y-1">
                            <Label
                              htmlFor={`title-${item.id}`}
                              className="text-xs"
                            >
                              Title (optional)
                            </Label>
                            <Input
                              id={`title-${item.id}`}
                              value={item.title ?? ''}
                              onChange={(e) =>
                                updateItemStatus(item.id, {
                                  title: e.target.value,
                                  detailsOpen: true,
                                })
                              }
                              placeholder="Enter presentation title"
                              disabled={!canEditFiles}
                              className="h-8 text-sm"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label
                              htmlFor={`desc-${item.id}`}
                              className="text-xs"
                            >
                              Description (optional)
                            </Label>
                            <Textarea
                              id={`desc-${item.id}`}
                              value={item.description ?? ''}
                              onChange={(e) =>
                                updateItemStatus(item.id, {
                                  description: e.target.value,
                                  detailsOpen: true,
                                })
                              }
                              placeholder="Enter presentation description"
                              rows={2}
                              disabled={!canEditFiles}
                              className="text-sm"
                            />
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            updateItemStatus(item.id, { detailsOpen: true })
                          }
                          disabled={!canEditFiles}
                          className="text-xs text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          Click to add title and description (optional)
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
              <p className="px-1 text-xs text-muted-foreground">
                {pendingCount > 0 && `${pendingCount} pending`}
                {succeededCount > 0 && `${pendingCount > 0 ? ' · ' : ''}${succeededCount} uploaded`}
                {failedCount > 0 && `${pendingCount > 0 || succeededCount > 0 ? ' · ' : ''}${failedCount} failed`}
              </p>
            </div>
          )}

          {/* Title/description only for single-file uploads. Multi-file
              uploads rely on the backend's filename fallback to keep the
              flow frictionless. */}
          {isSingleFile && items[0] && (
            <>
              <div className="space-y-2">
                <Label htmlFor="title">Title (optional)</Label>
                <Input
                  id="title"
                  value={items[0].title ?? ''}
                  onChange={(e) => updateItemStatus(items[0].id, { title: e.target.value })}
                  placeholder="Enter presentation title"
                  disabled={!canEditFiles}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description (optional)</Label>
                <Textarea
                  id="description"
                  value={items[0].description ?? ''}
                  onChange={(e) => updateItemStatus(items[0].id, { description: e.target.value })}
                  placeholder="Enter presentation description"
                  rows={2}
                  disabled={!canEditFiles}
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          {isComplete ? (
            <>
              {failedCount > 0 && (
                <Button
                  variant="outline"
                  onClick={handleRetryFailed}
                  disabled={isUploading}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Retry {failedCount} Failed
                </Button>
              )}
              <Button onClick={handleClose}>Close</Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isUploading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={items.length === 0 || isUploading}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading…
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    {items.length > 1
                      ? `Upload ${items.length} Presentations`
                      : 'Upload Presentation'}
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PresentationUploadDialog;
